import { useState, useMemo, useCallback } from 'react';
import {
  useListAgentSkills,
  useDisabledSkills,
} from '../../../../api/agent-skills';
import type { AgentSkill } from '../../../../api/agent-skills';
import { useUserProfile } from '../../../../api/profile';
import { useIsAdmin } from '../../../../components/onboarding-new/api';
import type { SkillManager, ScopeFilter } from '../types';
import { isSkillReadOnly } from '../utils/skillAccess';

export function useSkillManager(): SkillManager {
  const { data: userProfile } = useUserProfile();
  const currentUserId = userProfile?.id ?? null;
  const isAdmin = useIsAdmin();
  const { data: skills, isLoading } = useListAgentSkills();
  const { data: disabledSkillIds = [] } = useDisabledSkills();

  const [_selectedSkillName, setSelectedSkillName] = useState<string | null>(
    null,
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [showTemplateGallery, setShowTemplateGallery] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>('all');
  const [tagFilter, setTagFilter] = useState<string[]>([]);

  // Helper to create unique skill identifier from scope:name
  const getSkillId = (skill: AgentSkill) => `${skill.scope}:${skill.name}`;

  const getDisableId = (skill: AgentSkill) =>
    skill.isBuiltIn ? `builtin-${skill.name}` : skill.name;

  const builtIns = useMemo(
    () => (skills ?? []).filter((s) => s.isBuiltIn),
    [skills],
  );

  const userDefined = useMemo(
    () => (skills ?? []).filter((s) => !s.isBuiltIn),
    [skills],
  );

  const filterList = useCallback(
    (list: AgentSkill[]) => {
      let result = list;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        result = result.filter(
          (s) =>
            s.displayName.toLowerCase().includes(q) ||
            s.name.toLowerCase().includes(q),
        );
      }
      return result;
    },
    [searchQuery],
  );

  const filteredBuiltIns = useMemo(
    () => filterList(builtIns),
    [builtIns, filterList],
  );

  const filteredUserDefined = useMemo(
    () => filterList(userDefined),
    [userDefined, filterList],
  );

  const filteredMySkills = useMemo(
    () =>
      filteredUserDefined.filter(
        (s) =>
          s.scope === 'user' && currentUserId && s.createdBy === currentUserId,
      ),
    [filteredUserDefined, currentUserId],
  );

  const filteredOrgSkills = useMemo(
    () =>
      filteredUserDefined.filter((s) => s.scope === 'tenant' && !s.isBuiltIn),
    [filteredUserDefined],
  );

  const filteredTemplates = useMemo(
    () => filteredUserDefined.filter((s) => s.scope === 'template'),
    [filteredUserDefined],
  );

  const filteredPleriSkills = useMemo(
    () => filteredBuiltIns,
    [filteredBuiltIns],
  );

  const tagNames = useMemo(() => {
    const names = new Set<string>();
    for (const skill of skills ?? []) {
      for (const t of skill.tags ?? []) {
        names.add(t);
      }
    }
    return [...names].sort();
  }, [skills]);

  const visibleSkills = useMemo(() => {
    let list: AgentSkill[];
    switch (scopeFilter) {
      case 'my':
        list = filteredMySkills;
        break;
      case 'tenant':
        list = filteredOrgSkills;
        break;
      case 'template':
        list = filteredTemplates;
        break;
      case 'pleri':
        list = filteredPleriSkills;
        break;
      default:
        list = [
          ...filteredMySkills,
          ...filteredOrgSkills,
          ...filteredTemplates,
          ...filteredPleriSkills,
        ];
    }
    if (tagFilter.length > 0) {
      list = list.filter((s) => tagFilter.some((t) => s.tags?.includes(t)));
    }
    return list;
  }, [
    scopeFilter,
    tagFilter,
    filteredMySkills,
    filteredOrgSkills,
    filteredTemplates,
    filteredPleriSkills,
  ]);

  const isSkillReadOnlyForUser = useCallback(
    (skill: AgentSkill): boolean =>
      isSkillReadOnly(skill, { currentUserId, isAdmin }),
    [currentUserId, isAdmin],
  );

  return {
    // State
    searchQuery,
    showTemplateGallery,
    showImportDialog,
    scopeFilter,
    tagFilter,

    // Actions
    setSelectedSkillName,
    setSearchQuery,
    setShowTemplateGallery,
    setShowImportDialog,
    setScopeFilter,
    setTagFilter,
    getDisableId,
    getSkillId,

    // Data
    skills,
    isLoading,
    visibleSkills,
    disabledSkillIds,
    currentUserId,
    isAdmin,
    isSkillReadOnly: isSkillReadOnlyForUser,
    tagNames,
  };
}
